import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Light / dark theme for the learner app AND the admin site (one stored choice per host, the key below).
 * Round 145 (owner ruling 2026-10-25: every page has light and dark; supervisor default (a)): with no stored choice the theme FOLLOWS THE
 * DEVICE (prefers-color-scheme) and keeps following it if the device changes; once the visitor uses the switch, that pick is stored and wins.
 * The same rule runs before the first paint in frontend/index.html (a small inline script), so a dark-device visitor never sees a light flash;
 * keep the two in step (frontend/src/contexts/ThemeContext.test.tsx reads index.html). Every storage access is wrapped: private windows and
 * blocked storage fall back to the device.
 */
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'emi-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The visitor's own pick, or null when there is none (or storage cannot be read). */
export function readStoredTheme(): Theme | null {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

export function deviceTheme(): Theme {
  try { return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light'; } catch { return 'light'; }
}

/** What the page shows: the stored pick, else the device. */
export function resolveTheme(): Theme { return readStoredTheme() ?? deviceTheme(); }

export function applyTheme(theme: Theme): void {
  const el = document.documentElement;
  if (theme === 'dark') el.setAttribute('data-theme', 'dark'); else el.removeAttribute('data-theme');
  // native controls (checkboxes, selects, scrollbars) follow the theme too
  el.style.colorScheme = theme;
}

interface ThemeContextValue {
  theme: Theme;
  /** true once the visitor has picked (the device no longer decides) */
  chosen: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ theme: Theme; chosen: boolean }>(() => {
    const stored = readStoredTheme();
    return { theme: stored ?? deviceTheme(), chosen: stored !== null };
  });

  useEffect(() => { applyTheme(state.theme); }, [state.theme]);

  // with no pick, follow the device when it changes (e.g. the phone switches to dark at night)
  useEffect(() => {
    if (state.chosen) return undefined;
    let mql: MediaQueryList | undefined;
    try { mql = window.matchMedia?.(DARK_QUERY); } catch { mql = undefined; }
    if (!mql) return undefined;
    const onChange = (e: MediaQueryListEvent) => setState((s) => (s.chosen ? s : { theme: e.matches ? 'dark' : 'light', chosen: false }));
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange); else mql.addListener?.(onChange);
    return () => { if (typeof mql!.removeEventListener === 'function') mql!.removeEventListener('change', onChange); else mql!.removeListener?.(onChange); };
  }, [state.chosen]);

  const toggleTheme = useCallback(() => {
    setState((s) => {
      const theme: Theme = s.theme === 'light' ? 'dark' : 'light';
      try { window.localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* the pick still applies for this visit */ }
      return { theme, chosen: true };
    });
  }, []);

  return <ThemeContext.Provider value={{ theme: state.theme, chosen: state.chosen, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useThemeContext() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}

